import { useReducer, useEffect, useCallback } from 'react'
import { useShareUpdateWorkspacePermissionsMutation } from 'gqlTypes'
import { useToast } from 'containers/ToastManager'
import { ShareForSettingsModal } from '../types'
import { ErrorHandler } from 'modules/tracing'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { WorkspaceAccessLevelType } from '../AccessLevelSelect'

type Values = Pick<
  ShareForSettingsModal,
  'workspaceCommentsEnabled' | 'workspaceAccessLevel' | 'identifier'
>

type State = {
  initialValues: Values
  values: Values
  dialogOpen: boolean
  mutating: boolean
}

type Action =
  | { type: 'show-confirmation-modal' }
  | { type: 'hide-confirmation-modal' }
  | { type: 'reset' }
  | { type: 'set-level'; payload: { level: WorkspaceAccessLevelType } }
  | { type: 'set-comments-enabled'; payload: { enabled: boolean } }
  | { type: 'success'; payload: { values: Values } }
  | { type: 'set-mutating' }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'show-confirmation-modal':
      return { ...state, dialogOpen: true }
    case 'hide-confirmation-modal':
      return { ...state, dialogOpen: false }
    case 'set-mutating':
      return { ...state, mutating: true }
    case 'reset':
      return {
        ...state,
        dialogOpen: false,
        values: { ...state.initialValues },
        mutating: false,
      }
    case 'success':
      return {
        ...state,
        values: action.payload.values,
        initialValues: action.payload.values,
        mutating: false,
      }
    case 'set-level':
      return {
        ...state,
        values: {
          ...state.values,
          workspaceAccessLevel: action.payload.level,
        },
      }
    case 'set-comments-enabled':
      return {
        ...state,
        values: {
          ...state.values,
          workspaceCommentsEnabled: action.payload.enabled,
        },
      }
    default:
      return state
  }
}

interface UseUpdateWorkspaceProps {
  share: Values
}

const areValuesEqual = (a: Values, b: Values) =>
  a.workspaceAccessLevel === b.workspaceAccessLevel &&
  a.workspaceCommentsEnabled === b.workspaceCommentsEnabled

export const useUpdateWorkspace = ({ share }: UseUpdateWorkspaceProps) => {
  const { showToast } = useToast()
  const { hideModal } = useModalContext()

  const [
    { initialValues, values, dialogOpen, mutating },
    dispatch,
  ] = useReducer(reducer, {
    initialValues: share,
    values: share,
    dialogOpen: false,
    mutating: false,
  })

  const [
    shareUpdateWorkspacePermissions,
    { loading: updateWorkspaceLoading },
  ] = useShareUpdateWorkspacePermissionsMutation({
    onCompleted(data) {
      if (!data?.shareUpdateWorkspacePermissions) {
        ErrorHandler.shouldNeverHappen(
          'Share update workspace permissions mutation should return valid data'
        )
        dispatch({ type: 'reset' })
        return
      }
      if (
        data.shareUpdateWorkspacePermissions.userCanUpdateSettings === false
      ) {
        hideModal()
        showToast("You can no longer view this document's settings")
        return
      }
      dispatch({
        type: 'success',
        payload: { values: data.shareUpdateWorkspacePermissions },
      })
    },
    onError(error) {
      if (error.includesErrorCode('CONFIRMATION_REQUIRED')) {
        dispatch({ type: 'show-confirmation-modal' })
        return
      }

      dispatch({ type: 'reset' })
      showToast(
        'There was a problem changing the workspace permissions. Please try again.',
        'negative'
      )
    },
  })

  const mutate = useCallback(
    (confirmed: boolean = false) => {
      dispatch({ type: 'set-mutating' })
      return shareUpdateWorkspacePermissions({
        variables: {
          identifier: share.identifier,
          permissions: {
            confirmed,
            workspaceCommentsEnabled: values.workspaceCommentsEnabled,
            workspaceAccessLevel: values.workspaceAccessLevel,
          },
        },
      })
    },
    [share, values, shareUpdateWorkspacePermissions]
  )

  useEffect(() => {
    if (!mutating && !areValuesEqual(values, initialValues)) {
      mutate()
    }
  }, [values, initialValues, mutate, mutating])

  const getUpdateWorkspaceConfirmDialogProps = useCallback(
    () => ({
      hideModal() {
        dispatch({ type: 'reset' })
      },
      onConfirm() {
        dispatch({ type: 'hide-confirmation-modal' })
        mutate(true)
      },
      isModalOpen: true,
      confirmButton: {
        text: 'Confirm',
        intent: 'positive',
        loading: updateWorkspaceLoading,
      } as const,
    }),
    [dispatch, mutate, updateWorkspaceLoading]
  )

  const getUpdateWorkspaceDropdownProps = useCallback(
    () => ({
      commentsEnabled: values.workspaceCommentsEnabled,
      setCommentsEnabled: (enabled: boolean) => {
        dispatch({ type: 'set-comments-enabled', payload: { enabled } })
      },
      level: initialValues.workspaceAccessLevel,
      setLevel: (level: WorkspaceAccessLevelType) =>
        dispatch({ type: 'set-level', payload: { level } }),
      loading: updateWorkspaceLoading,
    }),
    [updateWorkspaceLoading, values, dispatch, initialValues]
  )

  return {
    updateWorkspaceLoading,
    getUpdateWorkspaceDropdownProps,
    getUpdateWorkspaceConfirmDialogProps,
    updateWorkspaceConfirmDialogOpen: dialogOpen,
    updateWorkspaceLevel: values.workspaceAccessLevel,
  }
}
